import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DatePicker from 'react-date-picker'
import moment from 'moment'
// import classNames from 'classnames'
import shortid from 'shortid'

import { ModalBody, ModalFooter } from './modal'
import MultiList from './multi-list'
import Input, { Label } from './input'
import { SelectBoxSimple } from './select-box'
import styles from '../component-styles/account-settings.module.scss'
import ConfirmationButtons from './confirmation-buttons'
import ToolTip from './tooltip'
import {
  getEnterpriseBillingState,
  updateEnterpriseBillingState,
} from '../store/actions'
import { FormRow, LabelSlot, FieldSlot } from './row'
import { GraphQLState } from '../store/graphql-reducer'
import { Preloader } from './loader'

const formShape = {
  companyName: {
    title: 'Client',
    type: 'input',
    tooltip: 'ToolTip',
  },
  businessDivision: {
    tooltip: 'ToolTip',
    optional: true,
    title: 'Business unit',
    type: 'input',
  },
  sponsorList: {
    tooltip: 'ToolTip',
    title: 'Sponsors',
    type: 'multi-list',
    placeholder: 'User name and surname',
    placeholderTwo: 'User email',
  },
  accountManagerList: {
    tooltip: 'ToolTip',
    title: 'Account manager(s)',
    type: 'multi-list',
    placeholder: 'Add account manager',
  },
  legalEntityName: {
    tooltip: 'ToolTip',
    title: 'Clients legal name',
    type: 'input',
  },
  contractStartDate: {
    title: 'Contract start date',
    type: 'date',
  },
  breakClauseDate: {
    tooltip: 'ToolTip',
    optional: true,
    title: 'Break clause date',
    type: 'date',
  },
  contractEndDate: {
    title: 'Contract end date',
    type: 'date',
  },
  totalContractValue: {
    tooltip: 'ToolTip',
    title: 'Total contract revenue',
    type: 'currency',
  },
  revenuePerMonth: {
    tooltip: 'ToolTip',
    title: 'Monthly revenue',
    type: 'currency',
  },
  customTandC: {
    tooltip: 'ToolTip',
    title: 'Custom Terms & Conditions?',
    type: 'toggle',
    label: ' ',
  },
  autoRenew: {
    tooltip: 'ToolTip',
    title: 'Automatic contract renewal?',
    label: ' ',
    type: 'toggle',
  },
  domains: {
    tooltip: 'ToolTip',
    optional: true,
    title: 'Website domains',
    type: 'textarea',
  },
  domainLimit: {
    tooltip: 'ToolTip',
    title: 'Paid domain limit',
    type: 'number',
  },
  userLimit: {
    tooltip: 'ToolTip',
    title: 'Paid users limit',
    type: 'number',
  },
  dataSourceType: {
    tooltip: 'ToolTip',
    title: 'Data source',
    type: 'select',
    options: [
      {
        name: 'Google Analytics',
        value: 'GA_VIEW',
      },
      {
        name: 'Adobe Analytics',
        value: 'AA_REPORT',
      },
    ],
  },
  trackModules: {
    tooltip: 'ToolTip',
    type: 'group',
    title: 'Paid modules',
    group: {
      trackAvailable: {
        title: 'Track',
        label: 'Module enabled',
        type: 'toggle',
      },
      auditAvailable: {
        title: 'Audit',
        label: 'Module enabled',
        type: 'toggle',
      },
      reportAvailable: {
        title: 'Report',
        label: 'Module enabled',
        type: 'toggle',
      },
      explainAvailable: {
        title: 'Explain',
        label: 'Module enabled',
        type: 'toggle',
      },
    },
  },
  companyNotes: {
    optional: true,
    title: 'Notes',
    type: 'textarea',
  },
}

interface Props {
  modal?: boolean
  fields: EnterpriseBillingDetails
  onCancel?: () => void
  onSave?: (fields: any) => void
  loading?: boolean
  readOnly?: boolean
}

export function TheForm({
  fields: intialFields,
  onCancel,
  onSave,
  modal,
  loading,
  readOnly = true,
}: Props) {
  const [fields, setFields] = useState(intialFields)
  const [updated, setUpdated] = useState(false)

  const currencySign = '£'
  const updateFields = (key, value) => {
    setUpdated(true)
    setFields({ ...fields, [key]: value })
  }
  if (loading) {
    return (
      <Preloader
        style={{ width: 90, height: 60, marginTop: 50, marginBottom: 50 }}
      />
    )
  }
  const getForm = () => {
    return (
      <>
        {Object.keys(formShape).map((key) => {
          const shapeItem = formShape[key]
          const { optional, type } = shapeItem

          const value = fields[key]

          switch (shapeItem.type) {
            case 'textarea':
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      {shapeItem.title}
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    <Input
                      readOnly={readOnly}
                      required={!shapeItem.optional}
                      id={key}
                      name={key}
                      type="textArea"
                      onChange={(val: string): void => {
                        updateFields(key, val)
                      }}
                      placeholder="Click here and start typing..."
                      value={value}
                    />
                  </FieldSlot>
                </FormRow>
              )
            case 'toggle':
            case 'group':
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      {shapeItem.title}
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    {type === 'group' ? (
                      <span className={styles.groupFields}>
                        {Object.keys(shapeItem.group).map((groupKey) => {
                          const groupShapeItem = shapeItem.group[groupKey]
                          return (
                            <span key={groupKey} className={styles.row}>
                              <Input
                                readOnly={readOnly}
                                type="checkbox"
                                id={groupKey}
                                name={groupKey}
                                label={groupShapeItem.title}
                                checked={fields[groupKey]}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ): void => {
                                  const {
                                    checked,
                                  } = e.target as HTMLInputElement
                                  updateFields(groupKey, checked)
                                }}
                              />
                            </span>
                          )
                        })}
                      </span>
                    ) : (
                      <Input
                        readOnly={readOnly}
                        type="checkbox"
                        id={key}
                        name={key}
                        label={shapeItem.label}
                        checked={value}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                        ): void => {
                          const { checked } = e.target as HTMLInputElement
                          updateFields(key, checked)
                        }}
                      />
                    )}
                  </FieldSlot>
                </FormRow>
              )
            case 'currency':
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      {shapeItem.title} ({currencySign})
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    <Input
                      readOnly={readOnly}
                      key={key}
                      required={!shapeItem.optional}
                      onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        const { value: val } = e.target as HTMLInputElement
                        const parsedVal = val ? parseInt(val, 10) : 0
                        updateFields(
                          key,
                          !Number.isNaN(parsedVal) ? parsedVal : 0,
                        )
                      }}
                      label={shapeItem.title}
                      type="text"
                      name={key}
                      id={key}
                      value={value}
                    />
                  </FieldSlot>
                </FormRow>
              )
            case 'number':
            case 'input':
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      <span>{shapeItem.title}</span>
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    <Input
                      readOnly={readOnly}
                      required={!shapeItem.optional}
                      autoComplete="off"
                      key={key}
                      onChange={(e: React.FormEvent<HTMLInputElement>) => {
                        const { value: val } = e.target as HTMLInputElement

                        if (shapeItem.type === 'number') {
                          const parsedVal = val ? parseInt(val, 10) : 0
                          updateFields(
                            key,
                            !Number.isNaN(parsedVal) ? parsedVal : 0,
                          )
                        } else {
                          updateFields(key, val)
                        }
                      }}
                      label={shapeItem.title}
                      type="text"
                      name={key}
                      id={key}
                      value={value}
                    />
                  </FieldSlot>
                </FormRow>
              )
            case 'date': {
              let dateValue: null | Date = null
              if (value !== null && value !== '') {
                const dateFormatted = moment(value, 'YYYYMMDD').format(
                  'YYYY-MM-DD',
                )
                dateValue = new Date(Date.parse(dateFormatted))
                // check if date invalid
                if (Number.isNaN(dateValue.getTime())) {
                  dateValue = null
                }
              }
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      {shapeItem.title}
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    <DatePicker
                      disabled={readOnly}
                      format="dd/MM/y"
                      key={key}
                      className="date-picker"
                      value={dateValue}
                      onChange={(date): void => {
                        if (date !== null) {
                          const dateF = moment(date.toString()).format(
                            'YYYYMMDD',
                          )
                          updateFields(key, dateF)
                        } else {
                          updateFields(key, date)
                        }
                      }}
                    />
                  </FieldSlot>
                </FormRow>
              )
            }
            case 'multi-list': {
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      {shapeItem.title}
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    <MultiList
                      readOnly={readOnly}
                      id={key}
                      ItemOneLabel="Full Name"
                      ItemTwoLabel="Email"
                      className={styles.table}
                      list={value}
                      onChange={(val) => {
                        updateFields(key, val)
                      }}
                    />
                  </FieldSlot>
                </FormRow>
              )
            }
            case 'select': {
              return (
                <FormRow key={key} wideFields>
                  <LabelSlot topAlign>
                    <Label
                      id={key}
                      optional={optional ? '(if applicable)' : ''}
                    >
                      {shapeItem.title}
                    </Label>
                    {shapeItem.tooltip && (
                      <ToolTip message={shapeItem.tooltip} />
                    )}
                  </LabelSlot>
                  <FieldSlot>
                    <SelectBoxSimple
                      disabled={readOnly}
                      className={styles.selecBox}
                      name={key}
                      key={key}
                      value={value}
                      onChange={(val) => {
                        updateFields(key, val)
                      }}
                    >
                      {shapeItem.options.map((i) => (
                        <option key={`${key}${i.value}`} value={i.value}>
                          {i.name}
                        </option>
                      ))}
                    </SelectBoxSimple>
                  </FieldSlot>
                </FormRow>
              )
            }
            default:
              return null
          }
        })}
      </>
    )
  }

  if (modal) {
    return (
      <>
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault()
            if (onSave) {
              onSave(fields)
            }
          }}
        >
          <ModalBody>{getForm()}</ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              onYes={(): void => {
                // if (onSave) {
                //   onSave(fields)
                // }
              }}
              onNo={(): void => {
                if (onCancel) {
                  onCancel()
                }
              }}
              yesLabel="Create a new client"
              noLabel="Go back"
            />
          </ModalFooter>
        </form>
      </>
    )
  }

  return (
    <>
      <form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault()
          if (onSave) {
            onSave(fields)
          }
        }}
      >
        {getForm()}
        <FormRow className={styles.noPadding} wideFields>
          <LabelSlot />
          <FieldSlot>
            <ConfirmationButtons
              disabled={!updated}
              type="simple"
              className={styles.confirmationButtons}
              onYes={(): void => {
                // if (onSave) {
                //   onSave(fields)
                // }
              }}
              onNo={(): void => {
                if (onCancel) {
                  onCancel()
                }
              }}
              yesLabel="Save changes"
              noLabel="Cancel"
            />
          </FieldSlot>
        </FormRow>
      </form>
    </>
  )
}

interface AccountSettingsProps {
  className?: string
}

export default function AccountSettings({ className }: AccountSettingsProps) {
  const readOnly = true

  const dispatch = useDispatch()
  const {
    graphql: { enterpriseBillingDetail, error: gqlErrors },
  }: { graphql: GraphQLState } = useSelector((s: any): any => s)
  const error = gqlErrors.indexOf('enterpriseBillingDetail') !== -1
  // const isEnterpriseAccount = billingType === 'enterprise'

  useEffect(() => {
    dispatch(getEnterpriseBillingState())
    // eslint-disable-next-line
  }, [])

  // const currencySign = useMemo(()=>{
  //   if (currency && currency !== '') {
  //     const item = getItemByKeyValue([
  //       {
  //         name: '£',
  //         value: 'GBP',
  //       },
  //     ], 'value', currency)
  //     if (item) {
  //       return item.name
  //     }
  //   }
  //   return '£'
  // }, [currency])

  return (
    <div className={className}>
      <>
        {!error && !enterpriseBillingDetail && (
          <Preloader
            style={{ width: 60, height: 40, marginTop: 50, marginBottom: 50 }}
          />
        )}
        {error && (
          <p className={styles.error}>
            There has been problem loading enterprise data for this account.
          </p>
        )}
        {enterpriseBillingDetail && (
          <>
            <h3>
              Contract details for - {enterpriseBillingDetail.companyName}
            </h3>
            <TheForm
              readOnly={readOnly}
              key={shortid.generate()}
              fields={enterpriseBillingDetail}
              onSave={(newData) => {
                dispatch(updateEnterpriseBillingState(newData))
              }}
            />
          </>
        )}
      </>
    </div>
  )
}
